import { Fragment } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';

import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';

export default function Step13OficiosExpedidos(props: RelatorioCivelStepProps) {
  const {
    cardStyle,
    headerStyle,
    RELATORIO_CORES,
    apenasNumeros,
    quantidadeLinhas,
    setQuantidadeLinhas,
    formData,
    renderTabelaProcessosSimples,
    isViewMode,

    // Requisitórios
    novoProcessoRequisitorio,
    setNovoProcessoRequisitorio,
    adicionarOficioRequisitorio,
    adicionarMultiplasLinhasOficiosRequisitorios,
    removerOficioRequisitorio,
    editandoOficioRequisitorio,
    valorEdicaoProcessoRequisitorio,
    setValorEdicaoProcessoRequisitorio,
    iniciarEdicaoOficioRequisitorio,
    salvarEdicaoOficioRequisitorio,
    cancelarEdicaoOficioRequisitorio,

    // Demais
    novoProcessoDemaisOficios,
    setNovoProcessoDemaisOficios,
    adicionarOficioDemais,
    adicionarMultiplasLinhasOficiosDemais,
    removerOficioDemais,
    editandoOficioDemais,
    valorEdicaoProcessoDemaisOficios,
    setValorEdicaoProcessoDemaisOficios,
    iniciarEdicaoOficioDemais,
    salvarEdicaoOficioDemais,
    cancelarEdicaoOficioDemais,
  } = props;

  return (
    <Fragment>
      {/* Sub-etapa 1: Requisitórios */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h6 className="mb-0">
            <i className="bi bi-file-earmark-arrow-up me-2"></i>
            Requisitórios
          </h6>
        </Card.Header>
        <Card.Body>
          {!isViewMode && (
            <>
              <Row className="mb-3">
                <Col md={10}>
                  <Form.Group>
                    <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Ex: 000000.000.000"
                        value={novoProcessoRequisitorio}
                        onChange={(e) => setNovoProcessoRequisitorio(apenasNumeros(e.target.value))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            adicionarOficioRequisitorio();
                          }
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        style={{ borderRadius: '8px 0 0 8px' }}
                      />
                      <Button
                        variant="primary"
                        onClick={adicionarOficioRequisitorio}
                        style={{ borderRadius: '0 8px 8px 0' }}
                      >
                        <i className="bi bi-plus-lg me-2"></i>
                        Adicionar
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Pressione Enter ou clique em Adicionar para incluir o processo na lista
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Adicionar</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosRequisitorios: Math.max(1, prev.oficiosRequisitorios - 1),
                        }))
                      }
                      style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    >
                      <i className="bi bi-dash">-</i>
                    </Button>
                    <Form.Control
                      type="number"
                      min="1"
                      max="50"
                      value={quantidadeLinhas.oficiosRequisitorios}
                      onChange={(e) =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosRequisitorios: Math.max(1, parseInt(e.target.value) || 1),
                        }))
                      }
                      style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                      size="sm"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosRequisitorios: Math.min(50, prev.oficiosRequisitorios + 1),
                        }))
                      }
                      style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    >
                      <i className="bi bi-plus">+</i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={adicionarMultiplasLinhasOficiosRequisitorios}
                      style={{ borderRadius: '8px' }}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Adicionar {quantidadeLinhas.oficiosRequisitorios} linha
                      {quantidadeLinhas.oficiosRequisitorios > 1 ? 's' : ''}
                    </Button>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {renderTabelaProcessosSimples(
            formData.oficiosExpedidos.requisitorios,
            removerOficioRequisitorio,
            editandoOficioRequisitorio,
            valorEdicaoProcessoRequisitorio,
            setValorEdicaoProcessoRequisitorio,
            iniciarEdicaoOficioRequisitorio,
            salvarEdicaoOficioRequisitorio,
            cancelarEdicaoOficioRequisitorio,
          )}

          <div className="mt-2 text-end">
            <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
              <small className="text-muted">Total de Processos:</small> {formData.oficiosExpedidos.requisitorios.length}
            </h5>
          </div>
        </Card.Body>
      </Card>

      {/* Sub-etapa 2: Demais Ofícios */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h6 className="mb-0">
            <i className="bi bi-file-earmark-text me-2"></i>
            Demais Ofícios
          </h6>
        </Card.Header>
        <Card.Body>
          {!isViewMode && (
            <>
              <Row className="mb-3">
                <Col md={10}>
                  <Form.Group>
                    <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Ex: 000000.000.000"
                        value={novoProcessoDemaisOficios}
                        onChange={(e) => setNovoProcessoDemaisOficios(apenasNumeros(e.target.value))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            adicionarOficioDemais();
                          }
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        style={{ borderRadius: '8px 0 0 8px' }}
                      />
                      <Button variant="primary" onClick={adicionarOficioDemais} style={{ borderRadius: '0 8px 8px 0' }}>
                        <i className="bi bi-plus-lg me-2"></i>
                        Adicionar
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Pressione Enter ou clique em Adicionar para incluir o processo na lista
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Adicionar</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosDemais: Math.max(1, prev.oficiosDemais - 1),
                        }))
                      }
                      style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    >
                      <i className="bi bi-dash">-</i>
                    </Button>
                    <Form.Control
                      type="number"
                      min="1"
                      max="50"
                      value={quantidadeLinhas.oficiosDemais}
                      onChange={(e) =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosDemais: Math.max(1, parseInt(e.target.value) || 1),
                        }))
                      }
                      style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                      size="sm"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setQuantidadeLinhas((prev) => ({
                          ...prev,
                          oficiosDemais: Math.min(50, prev.oficiosDemais + 1),
                        }))
                      }
                      style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    >
                      <i className="bi bi-plus">+</i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={adicionarMultiplasLinhasOficiosDemais}
                      style={{ borderRadius: '8px' }}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Adicionar {quantidadeLinhas.oficiosDemais} linha{quantidadeLinhas.oficiosDemais > 1 ? 's' : ''}
                    </Button>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {renderTabelaProcessosSimples(
            formData.oficiosExpedidos.demais,
            removerOficioDemais,
            editandoOficioDemais,
            valorEdicaoProcessoDemaisOficios,
            setValorEdicaoProcessoDemaisOficios,
            iniciarEdicaoOficioDemais,
            salvarEdicaoOficioDemais,
            cancelarEdicaoOficioDemais,
          )}

          <div className="mt-2 text-end">
            <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
              <small className="text-muted">Total de Processos:</small> {formData.oficiosExpedidos.demais.length}
            </h5>
          </div>
        </Card.Body>
      </Card>
    </Fragment>
  );
}

